import { ImagePaths } from '../../lib/imageUrls.js';

export const aiGovernanceRiskCompliance = {

    id: 12,
    slug: 'ai-governance-risk-compliance',
    ogImage: '/og/courses/ai-governance-risk-compliance.jpg',
    courseCode: 'AI-GRC-001',
    title: 'AI Compliance: Governance, Risk & Security',
    subtitle: 'Technical deep-dive for AI security and risk management',
    valueProposition: 'Master AI-specific security threats, data governance frameworks, and robust GenAI policy implementation to build enterprise-grade AI risk management plans for Australian organisations.',
    duration: '80 hours',
    students: 'New',
    level: 'Advanced',
    image: ImagePaths.COURSE_4,
    shortDescription: 'AI-GRC-001 — AI governance, risk, and compliance covering ISO 42001, Essential Eight, and Privacy Act for security and compliance teams.',
    price: {
      aud: 1999,
      currency: 'AUD',
      earlyBird: 1749
    },
    stripeConfig: {
    priceId: 'price_1T9bE4Ku1nyc322UnynHKbMd',
    productId: 'prod_U7qvtn3JffYDCq'
  },
    
    snapshot: {
      whoItsFor: [
        'IT Administrators managing AI infrastructure',
        'Security Professionals implementing AI controls',
        'Compliance Officers overseeing AI governance',
        'AI Policy Makers designing organisational frameworks',
        'Risk managers evaluating AI adoption'
      ],
      accreditation: 'Aligned to ISO 27001, ISO 42001, Essential Eight, and Australian Privacy Principles'
    },
    
    logistics: {
      classSize: 'Maximum 12 participants for intensive hands-on labs',
      requirements: [
        'Strong understanding of cybersecurity fundamentals',
        'Experience with security frameworks (ISO 27001, NIST, Essential Eight)',
        'Familiarity with data governance and privacy regulations',
        'Laptop with administrative access for security labs',
        'Minimum 3 years experience in IT security or compliance roles'
      ]
    },
    
    overview: 'This intensive advanced course equips IT administrators, security professionals, and compliance officers with the technical expertise to build comprehensive AI risk management plans. You\'ll master AI-specific security threats including data poisoning, model inversion, adversarial attacks, and prompt injection exploits. Through hands-on labs and real-world scenarios, you\'ll implement robust data governance frameworks, secure LLM deployments, and create practical GenAI policies aligned to Australian regulatory requirements including the Privacy Act, Essential Eight, ISO 27001, ISO 42001, and the APPs. This is the definitive technical training for teams responsible for securing AI systems in Australian enterprises.',
    
    outcomes: [
      'Identify and mitigate AI-specific security threats including data poisoning, model inversion, and adversarial attacks',
      'Implement advanced prompt injection defences and LLM security controls',
      'Design and deploy comprehensive data governance frameworks for AI systems',
      'Conduct AI risk assessments aligned to ISO 27001, ISO 42001, and Essential Eight',
      'Create robust organisational AI policies addressing privacy, security, and compliance',
      'Evaluate and secure third-party AI services and APIs against Australian regulatory requirements',
      'Monitor and audit AI system behaviour for security anomalies and compliance violations',
      'Build incident response procedures specific to AI security breaches and model compromises'
    ],
    
    audience: [
      'IT Administrators managing AI infrastructure and services',
      'Security Professionals implementing AI security controls',
      'Compliance Officers overseeing AI governance programs',
      'AI Policy Makers designing organisational frameworks',
      'Risk Managers evaluating AI adoption and threats',
      'Security Architects integrating AI into security architecture',
      'Data Governance Specialists managing AI data pipelines'
    ],
    
    prerequisites: [
      'Strong foundation in cybersecurity principles and practices',
      'Experience with security frameworks (ISO 27001, NIST CSF, Essential Eight)',
      'Understanding of data governance and privacy regulations',
      'Minimum 3 years experience in IT security, compliance, or risk management',
      'Familiarity with cloud security and API architectures'
    ],
    
    modules: [
      {
        title: 'AI Security Threat Landscape',
        duration: '10 hours',
        topics: [
          'AI-specific attack vectors and threat models',
          'Data poisoning and training data manipulation',
          'Model inversion and membership inference attacks',
          'Adversarial machine learning and evasion techniques',
          'Supply chain risks in AI model deployment',
          'Emerging threats in generative AI systems'
        ]
      },
      {
        title: 'Advanced Prompt Injection Defence',
        duration: '12 hours',
        topics: [
          'Understanding prompt injection attack patterns',
          'Input validation and sanitisation for LLMs',
          'Context separation and privilege boundaries',
          'Implementing secure prompt templates',
          'Defence-in-depth strategies for conversational AI',
          'Monitoring and detecting prompt exploitation attempts'
        ]
      },
      {
        title: 'LLM Security and API Protection',
        duration: '11 hours',
        topics: [
          'Securing Large Language Model deployments',
          'API authentication and authorisation for AI services',
          'Rate limiting and abuse prevention',
          'Model access controls and privilege management',
          'Securing fine-tuning and model customisation pipelines',
          'Third-party AI service risk assessment'
        ]
      },
      {
        title: 'Data Governance for AI Systems',
        duration: '13 hours',
        topics: [
          'Data classification and sensitivity labelling for AI',
          'Privacy-preserving AI techniques (differential privacy, federated learning)',
          'Australian Privacy Principles (APPs) compliance in AI',
          'Data retention, lineage, and auditability',
          'Managing personal and sensitive information in training data',
          'Cross-border data transfer considerations for AI'
        ]
      },
      {
        title: 'AI Risk Assessment and Frameworks',
        duration: '12 hours',
        topics: [
          'Conducting AI-specific risk assessments',
          'ISO 27001 controls adapted for AI systems',
          'ISO 42001 AI management system implementation',
          'Essential Eight maturity assessment for AI infrastructure',
          'Threat modelling for machine learning pipelines',
          'Quantitative risk analysis for AI deployments'
        ]
      },
      {
        title: 'AI Policy Development and Implementation',
        duration: '10 hours',
        topics: [
          'Designing organisational GenAI acceptable use policies',
          'Balancing innovation with security and compliance',
          'Policy enforcement mechanisms and technical controls',
          'Employee training and awareness programs',
          'Third-party AI vendor assessment criteria',
          'Policy templates and Australian regulatory alignment'
        ]
      },
      {
        title: 'AI Monitoring, Auditing, and Incident Response',
        duration: '9 hours',
        topics: [
          'Continuous monitoring of AI system behaviour',
          'Logging and audit trails for AI activities',
          'Anomaly detection in AI inputs and outputs',
          'Investigating AI security incidents',
          'Model compromise and breach response procedures',
          'Regulatory reporting obligations for AI incidents'
        ]
      },
      {
        title: 'Building the AI Risk Management Plan',
        duration: '3 hours',
        topics: [
          'Components of a comprehensive AI risk management plan',
          'Aligning AI governance to organisational risk appetite',
          'Stakeholder communication and executive reporting',
          'Continuous improvement and adaptation',
          'Australian case studies and real-world implementations'
        ]
      }
    ],
    
    format: {
      type: 'Self-paced certification path + intensive 2-day virtual bootcamps',
      includes: [
        '80 hours of comprehensive technical content',
        'Hands-on security labs and scenario-based exercises',
        'Two 2-day intensive virtual bootcamps with expert instructors',
        'AI threat intelligence briefings and case studies',
        'Risk assessment templates and policy frameworks',
        'Access to AI security testing tools and environments',
        'Certificate of completion',
        '12 months access to course materials and updates',
        'Quarterly follow-up sessions on emerging AI threats',
        'Membership in AI security professional community'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Self-paced modules available immediately; intensive 2-day bootcamps scheduled quarterly'
    },
    
    // Pathway relationships
    pathways: [
      'ai-governance-pathway',
      'ai-security-pathway'
    ],

    skillsTaught: [
      'AI risk assessment',
      'Governance frameworks (ISO 42001, ISO 27001)',
      'AI policy development',
      'Compliance for Australian regulations',
      'AI security controls and monitoring'
    ],

    hasCapstone: true,

    nextCourses: [
      'ai-security-pathway'
    ],
    
    faqs: [
      {
        question: 'What makes this course different from general security training?',
        answer: 'This course focuses specifically on AI and GenAI security threats that traditional security training doesn\'t cover—data poisoning, model inversion, adversarial attacks, and prompt injection. These are emerging threats requiring specialised knowledge and mitigation strategies unique to AI systems.'
      },
      {
        question: 'Is this course technical? Do I need programming skills?',
        answer: 'Yes, this is a technical course for IT and security professionals. While you don\'t need to be a developer, you should have strong technical foundations in cybersecurity, networking, and systems administration. Familiarity with APIs, cloud services, and security frameworks is essential.'
      },
      {
        question: 'How does this align with Australian compliance requirements?',
        answer: 'The course is deeply grounded in Australian context including the Privacy Act, Australian Privacy Principles (APPs), Essential Eight maturity model, ISO 27001, and ISO 42001 (AI management systems). All policy templates, risk assessments, and examples reflect Australian regulatory obligations.'
      },
      {
        question: 'Will I be able to build an AI risk management plan for my organisation?',
        answer: 'Yes, that\'s the primary outcome. You\'ll work through building a comprehensive risk management plan throughout the course, using templates and frameworks that you can adapt directly to your organisation. The final module brings everything together into a complete, actionable plan.'
      },
      {
        question: 'What are the intensive 2-day bootcamps?',
        answer: 'Bootcamps are live virtual sessions where you\'ll work through complex scenarios, conduct hands-on security labs, and collaborate with instructors and peers on real-world AI security challenges. They complement the self-paced content with interactive, intensive practice.'
      },
      {
        question: 'How much time should I allocate each week for the self-paced content?',
        answer: 'We recommend 8-10 hours per week over 8-10 weeks. This is advanced material requiring study time beyond watching videos—you\'ll be implementing security controls, analysing threats, and building policy frameworks.'
      },
      {
        question: 'Can this course be customised for in-house delivery?',
        answer: 'Absolutely. Many organisations bring us in to deliver this training customised to their specific AI tools, infrastructure, and risk environment. We can tailor content to your industry, regulatory obligations, and existing security frameworks. Contact us for enterprise options.'
      },
      {
        question: 'Do you cover specific AI tools like ChatGPT, Copilot, or AWS Bedrock?',
        answer: 'We teach principles and controls applicable to any AI system, then demonstrate with commonly deployed tools including ChatGPT, Microsoft Copilot, Azure OpenAI, AWS Bedrock, and Google Vertex AI. You\'ll learn to assess and secure any AI service your organisation uses.'
      }
    ]
};
